// Volume Tracking System
// Tracks total training volume (tonnage) per workout, per week, and per muscle group
// Validates progressive overload and identifies stagnation
//
// IMPORTANT: This module exports shared tonnage calculation functions used by:
// - Dashboard volume display
// - ACWR calculation (recoveryDebt.ts)
// - Volume Intelligence component
// Changes here affect all these systems.

import { SessionLog, ExerciseLog } from './sessionStorage';
import { getExerciseMetadata, isPerHandLoad, type MuscleGroup } from './exerciseDatabase';

export interface VolumeMetrics {
  sessionTotalVolume: number;         // Total lbs lifted in session
  weeklyTotalVolume: number;          // Total lbs this week
  weeklyVolumeByMuscle: Record<string, number>;
  volumeTrend: 'increasing' | 'stagnant' | 'decreasing';
  volumeLandmarks: {
    allTimeHighVolume: number;
    last4WeekAverage: number;
    thisWeekVolume: number;
    percentChange: number;            // Week-over-week change
  };
  progressiveOverload: boolean;       // Is volume increasing over time?
}

export interface ExerciseVolumeStatus {
  exerciseName: string;
  currentVolume: number;
  lastWeekVolume: number;
  percentChange: number;
  status: 'increasing' | 'stagnant' | 'decreasing';
  weeksStagnant: number;
}

export interface OverloadStatus {
  isOnTrack: boolean;
  weeksStagnant: number;
  recommendation: string;
}

/**
 * Calculate total volume for a single set
 * Volume = Weight × Reps
 */
function calculateSetVolume(weight: number, reps: number): number {
  return weight * reps;
}

/**
 * Canonical per-set working volume — the SINGLE source of truth for tonnage
 * across the app. Applies both doubling axes consistently:
 *  - unilateral: `reps` is per-side → count L + R (rightReps if asymmetric)
 *  - per-hand (two dumbbells): logged weight is per hand → × 2
 * The two are mutually exclusive (unilateral wins; per-hand is guarded off) so
 * they never stack to 4×. Per-hand is read from the set's stamped `perHand`
 * flag when present, else inferred from `exerciseName` (covers legacy sets).
 * Does NOT include drop segments — callers that track drops add them.
 */
export function computeSetVolume(
  set: { weight?: number; reps?: number; unilateral?: boolean; rightReps?: number; perHand?: boolean },
  exerciseName?: string
): number {
  const weight = set.weight ?? 0;
  const reps = set.reps ?? 0;
  if (weight <= 0 || reps <= 0) return 0;
  const totalReps = set.unilateral
    ? reps + (typeof set.rightReps === 'number' ? set.rightReps : reps)
    : reps;
  const perHand = set.unilateral
    ? false
    : (typeof set.perHand === 'boolean' ? set.perHand : (exerciseName ? isPerHandLoad(exerciseName) : false));
  return weight * (perHand ? 2 : 1) * totalReps;
}

/**
 * Calculate total volume for a single exercise log
 * Handles per-set tracking, superset rounds, and legacy single-value tracking
 */
export function calculateExerciseVolume(exercise: ExerciseLog): number {
  let totalVolume = 0;

  // Two-dumbbell per-hand loading → true volume is weight × reps × 2. Prefer the
  // per-set flag (self-describing history); fall back to inferring from the name
  // for legacy sets. isPerHandLoad already returns false for unilateral movements
  // (they double via the per-side reps path), so the two never stack.
  const exPerHand = isPerHandLoad(exercise.exerciseName);
  const perHandMult = (setAny: any): number =>
    (setAny?.unilateral ? false : (typeof setAny?.perHand === 'boolean' ? setAny.perHand : exPerHand)) ? 2 : 1;

  // Superset rounds (for circuit/superset exercises)
  if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
    (exercise as any).supersetRounds.forEach((round: any) => {
      if (round.exercises && round.exercises.length > 0) {
        round.exercises.forEach((ex: any) => {
          if (ex.weight > 0 && ex.reps > 0) {
            totalVolume += calculateSetVolume(ex.weight * perHandMult(ex), ex.reps);
          }
        });
      }
    });
  }
  // Per-set tracking (preferred). Intentionally INCLUDES all set kinds
  // (drop / AMRAP / rest-pause / myo-rep / warmup) — they are all real
  // work performed and should count toward total volume. Only e1RM,
  // PR detection, and auto-progression filter to kind === 'normal'.
  // Unilateral sets count both sides (reps is per-side, optional
  // rightReps when asymmetric).
  else if (exercise.sets && exercise.sets.length > 0) {
    totalVolume = exercise.sets
      .filter(set => set.weight > 0 && set.reps > 0)  // Count any set with weight and reps, regardless of completed flag
      .reduce((sum, set) => {
        const setAny = set as any;
        const mult = perHandMult(setAny);
        let setVol = computeSetVolume(set, exercise.exerciseName);
        // Drop-set live segments: when the user fails mid-set and
        // drops the weight to keep going, each segment is real work
        // against a lighter load. Sum them in (same per-hand basis).
        if (setAny.dropSegments && Array.isArray(setAny.dropSegments)) {
          for (const seg of setAny.dropSegments) {
            if (seg && seg.weight > 0 && seg.reps > 0) {
              setVol += calculateSetVolume(seg.weight * mult, seg.reps);
            }
          }
        }
        return sum + setVol;
      }, 0);
  }
  // Legacy single-value tracking
  else if (exercise.actualLoad && exercise.actualReps) {
    const numSets = parseInt(exercise.targetSets.split('x')[0] || '1');
    totalVolume = calculateSetVolume(exercise.actualLoad * (exPerHand ? 2 : 1), exercise.actualReps) * numSets;
  }

  return totalVolume;
}

/**
 * Calculate "effective volume" for a single exercise log
 * Effective volume = volume × (RPE / 10)
 * 
 * This accounts for effort level - a set at RPE 9 is more stimulating than RPE 6
 * Research shows sets taken within 3 reps of failure (RPE 7+) are most effective for hypertrophy
 */
export function calculateEffectiveVolume(exercise: ExerciseLog): number {
  let effectiveVolume = 0;

  // Superset rounds (for circuit/superset exercises)
  if ((exercise as any).supersetRounds && (exercise as any).supersetRounds.length > 0) {
    (exercise as any).supersetRounds.forEach((round: any) => {
      if (round.exercises && round.exercises.length > 0) {
        round.exercises.forEach((ex: any) => {
          if (ex.weight > 0 && ex.reps > 0) {
            const setVolume = calculateSetVolume(ex.weight, ex.reps);
            const rpe = ex.rpe || 7; // Default to RPE 7 if not provided
            const effectiveMultiplier = rpe / 10;
            effectiveVolume += setVolume * effectiveMultiplier;
          }
        });
      }
    });
  }
  // Per-set tracking (preferred)
  else if (exercise.sets && exercise.sets.length > 0) {
    effectiveVolume = exercise.sets
      .filter(set => set.weight > 0 && set.reps > 0)  // Count any set with weight and reps
      .reduce((sum, set) => {
        const setVolume = calculateSetVolume(set.weight, set.reps);
        const rpe = set.rpe || 7; // Default to RPE 7 if not provided
        const effectiveMultiplier = rpe / 10;
        return sum + (setVolume * effectiveMultiplier);
      }, 0);
  } 
  // Legacy single-value tracking
  else if (exercise.actualLoad && exercise.actualReps) {
    const numSets = parseInt(exercise.targetSets.split('x')[0] || '1');
    const setVolume = calculateSetVolume(exercise.actualLoad, exercise.actualReps) * numSets;
    const rpe = exercise.actualRPE || 7;
    const effectiveMultiplier = rpe / 10;
    effectiveVolume = setVolume * effectiveMultiplier;
  }

  return effectiveVolume;
}

/**
 * Calculate volume for loaded carries and rucks (Farmer's Carry, Ruck March, etc.)
 * Volume = Weight × Distance (in meters) / 10 
 * This gives a volume equivalent: 100lbs carried 100m = 1000 volume units
 * 
 * NOTE: For rucks, this includes both ruck weight AND plate carrier weight
 */
function calculateCarryVolume(exercise: ExerciseLog): number {
  if (!exercise.actualLoad || !exercise.actualDistance) return 0;
  
  // Include plate carrier weight if available (for rucks)
  const plateCarrierWeight = (exercise as any).plateCarrierWeight || 0;
  const totalWeight = exercise.actualLoad + plateCarrierWeight;
  
  // Weight × Distance / 10 to normalize to a reasonable volume metric
  // Example: 40lbs ruck × 2574m (1.6 miles) / 10 = 10,296 volume units
  return (totalWeight * exercise.actualDistance) / 10;
}

/**
 * Calculate total volume for a session
 * Returns total tonnage (lbs) lifted across all exercises
 * Includes both traditional weight training AND loaded carries
 * 
 * NOTE: We count ALL exercises with weight/reps data, regardless of exerciseType.
 * This matches the ACWR calculation logic and ensures consistency.
 */
export function calculateSessionVolume(exercises: ExerciseLog[]): number {
  let totalVolume = 0;
  
  exercises.forEach(ex => {
    // First, try to calculate weight-based volume (works for any exercise with sets/reps)
    const exerciseVolume = calculateExerciseVolume(ex);
    if (exerciseVolume > 0) {
      totalVolume += exerciseVolume;
    }
    // Additionally check for loaded carries and rucks (distance-based volume)
    else if (ex.exerciseType === 'cardio' || ex.exerciseType === 'ruck' || ex.exerciseType === 'carry') {
      const nameLower = ex.exerciseName.toLowerCase();
      // Include rucks, carries, and loaded cardio
      if ((nameLower.includes('ruck') || nameLower.includes('carry') || 
           nameLower.includes('farmer') || nameLower.includes('suitcase')) 
          && ex.actualLoad && ex.actualDistance) {
        totalVolume += calculateCarryVolume(ex);
      }
    }
  });
  
  return totalVolume;
}

/**
 * Calculate volume by muscle group for a session
 * Uses exercise metadata to map exercises to muscle groups
 * Includes both traditional weight training AND loaded carries
 */
export function calculateVolumeByMuscle(exercises: ExerciseLog[]): Record<string, number> {
  const volumeByMuscle: Record<string, number> = {};

  exercises.forEach(ex => {
    // Handle superset rounds differently - attribute volume to individual exercises
    if ((ex as any).supersetRounds && (ex as any).supersetRounds.length > 0) {
      (ex as any).supersetRounds.forEach((round: any) => {
        if (round.exercises && round.exercises.length > 0) {
          round.exercises.forEach((subEx: any) => {
            if (subEx.weight > 0 && subEx.reps > 0) {
              const metadata = getExerciseMetadata(subEx.name);
              const volume = calculateSetVolume(subEx.weight, subEx.reps);
              
              if (metadata?.primaryMuscles && metadata.primaryMuscles.length > 0) {
                metadata.primaryMuscles.forEach(muscle => {
                  const muscleKey = muscle.toLowerCase();
                  volumeByMuscle[muscleKey] = (volumeByMuscle[muscleKey] || 0) + volume;
                });
              } else {
                // Fallback: categorize as "other"
                volumeByMuscle['other'] = (volumeByMuscle['other'] || 0) + volume;
              }
            }
          });
        }
      });
    } else {
      // Regular exercises (not supersets)
      const metadata = getExerciseMetadata(ex.exerciseName);
      
      // First try weight-based volume (works for any exercise with sets/reps)
      let volume = calculateExerciseVolume(ex);
      
      // If no weight volume, check for loaded carries
      if (volume === 0 && (ex.exerciseType === 'cardio' || ex.exerciseType === 'ruck' || ex.exerciseType === 'carry')) {
        const nameLower = ex.exerciseName.toLowerCase();
        if ((nameLower.includes('carry') || nameLower.includes('farmer') || nameLower.includes('suitcase')) 
            && ex.actualLoad && ex.actualDistance) {
          volume = calculateCarryVolume(ex);
        }
      }
      
      if (volume > 0) {
        if (metadata?.primaryMuscles && metadata.primaryMuscles.length > 0) {
          metadata.primaryMuscles.forEach(muscle => {
            const muscleKey = muscle.toLowerCase();
            volumeByMuscle[muscleKey] = (volumeByMuscle[muscleKey] || 0) + volume;
          });
        } else {
          // Fallback: categorize as "other"
          volumeByMuscle['other'] = (volumeByMuscle['other'] || 0) + volume;
        }
      }
    }
  });

  return volumeByMuscle;
}

// Major trainable muscle groups we surface for "what haven't you trained?"
// (skips full-body / cardio-specific / the overlapping abs + obliques).
const RECENCY_MUSCLES: MuscleGroup[] = [
  'chest', 'back', 'shoulders', 'biceps', 'triceps',
  'quads', 'hamstrings', 'glutes', 'calves', 'core',
];

export interface MuscleRecency {
  muscle: MuscleGroup;
  /** Days since this muscle was last trained; null = not seen in the history. */
  daysSince: number | null;
}

/**
 * Days since each major muscle was last trained, stalest first (null = never
 * in the provided history). Powers "recommend a muscle you haven't hit lately"
 * when adding an exercise mid-workout.
 */
export function getMuscleRecency(sessions: SessionLog[]): MuscleRecency[] {
  const lastMs: Partial<Record<MuscleGroup, number>> = {};
  for (const s of sessions) {
    const raw = (s as any).date;
    const t = raw ? new Date(raw).getTime() : NaN;
    if (Number.isNaN(t)) continue;
    for (const ex of ((s as any).exercises || []) as any[]) {
      const meta = getExerciseMetadata((ex as any).exerciseName || (ex as any).name || '');
      for (const m of meta?.primaryMuscles || []) {
        if (lastMs[m] === undefined || t > (lastMs[m] as number)) lastMs[m] = t;
      }
    }
  }
  const now = Date.now();
  return RECENCY_MUSCLES
    .map(m => {
      const t = lastMs[m];
      return { muscle: m, daysSince: t ? Math.floor((now - t) / 86_400_000) : null };
    })
    .sort((a, b) => (b.daysSince ?? 100_000) - (a.daysSince ?? 100_000));
}

/**
 * Get sessions from a specific week
 * Week is determined by comparing session dates
 */
function getSessionsInWeek(sessions: SessionLog[], targetWeek: number): SessionLog[] {
  return sessions.filter(s => s.week === targetWeek);
}

/**
 * Get sessions from the last N calendar weeks (7-day periods)
 * Uses actual calendar weeks instead of program week numbers
 */
function getSessionsInLastNWeeks(sessions: SessionLog[], weeks: number): SessionLog[][] {
  if (sessions.length === 0) return [];

  // Sort by date (newest first)
  const sorted = [...sessions].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const now = new Date();
  const weekArrays: SessionLog[][] = [];

  // Helper function to get Monday of a given week
  const getMondayOfWeek = (date: Date, weeksAgo: number): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() - (weeksAgo * 7)); // Go back N weeks
    const dayOfWeek = d.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days; otherwise go back to Monday
    d.setDate(d.getDate() - daysFromMonday);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Create N week buckets (Monday-Sunday calendar weeks)
  for (let i = 0; i < weeks; i++) {
    const weekStart = getMondayOfWeek(now, i);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Sunday
    weekEnd.setHours(23, 59, 59, 999);

    const weekSessions = sorted.filter(session => {
      const sessionDate = new Date(session.date);
      const isInWeek = sessionDate >= weekStart && sessionDate <= weekEnd;
      
      // Only count sessions with actual exercises (exclude skipped sessions)
      const hasExercises = session.sessionType === 'range' 
        ? true // Range sessions don't have exercises array
        : session.exercises && session.exercises.length > 0;
      
      return isInWeek && hasExercises;
    });

    weekArrays.push(weekSessions);
    
    // Debug log for first week only
    if (i === 0) {
      console.log(` This Week (Mon-Sun): ${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`);
      console.log(` Sessions in this week: ${weekSessions.length} (skipped sessions excluded)`);
      console.log(` Session dates:`, weekSessions.map(s => new Date(s.date).toLocaleDateString()));
    }
  }

  return weekArrays;
}

/**
 * Calculate weekly volume metrics
 * Analyzes volume trends over the last 4 weeks
 */
export function calculateWeeklyVolume(sessions: SessionLog[]): VolumeMetrics {
  const last4Weeks = getSessionsInLastNWeeks(sessions, 4);
  
  // Calculate weekly volumes
  const weeklyVolumes = last4Weeks.map(weekSessions => 
    weekSessions.reduce((sum, session) => {
      if (session.sessionType === 'range' || !session.exercises) return sum; // Type guard
      return sum + calculateSessionVolume(session.exercises);
    }, 0)
  );

  const thisWeekVolume = weeklyVolumes[0] || 0;
  const lastWeekVolume = weeklyVolumes[1] || 0;
  const last4WeekAverage = weeklyVolumes.length > 0
    ? weeklyVolumes.reduce((a, b) => a + b, 0) / weeklyVolumes.length
    : 0;
  const allTimeHighVolume = Math.max(...weeklyVolumes, 0);

  // Calculate percent change (week-over-week)
  const percentChange = lastWeekVolume > 0
    ? ((thisWeekVolume - lastWeekVolume) / lastWeekVolume) * 100
    : 0;

  // Analyze trend
  const volumeTrend = analyzeVolumeTrend(weeklyVolumes);

  // Calculate volume by muscle for current week
  const thisWeekSessions = last4Weeks[0] || [];
  const weeklyVolumeByMuscle = thisWeekSessions.reduce((acc, session) => {
    if (session.sessionType === 'range' || !session.exercises) return acc; // Type guard
    const sessionMuscleVolume = calculateVolumeByMuscle(session.exercises);
    Object.entries(sessionMuscleVolume).forEach(([muscle, volume]) => {
      acc[muscle] = (acc[muscle] || 0) + volume;
    });
    return acc;
  }, {} as Record<string, number>);

  // Check for progressive overload (volume increasing over time)
  const progressiveOverload = weeklyVolumes.length >= 2 && 
    weeklyVolumes[0] > weeklyVolumes[weeklyVolumes.length - 1] * 0.95; // Allow 5% variance

  return {
    sessionTotalVolume: 0, // Will be set per session
    weeklyTotalVolume: thisWeekVolume,
    weeklyVolumeByMuscle,
    volumeTrend,
    volumeLandmarks: {
      allTimeHighVolume,
      last4WeekAverage,
      thisWeekVolume,
      percentChange,
    },
    progressiveOverload,
  };
}

/**
 * Analyze volume trend from weekly data
 * Determines if volume is increasing, stagnant, or decreasing
 */
export function analyzeVolumeTrend(weeklyVolumes: number[]): 'increasing' | 'stagnant' | 'decreasing' {
  if (weeklyVolumes.length < 2) return 'stagnant';

  // Compare last 2 weeks
  const thisWeek = weeklyVolumes[0];
  const lastWeek = weeklyVolumes[1];
  const percentChange = lastWeek > 0 ? ((thisWeek - lastWeek) / lastWeek) * 100 : 0;

  if (percentChange > 3) return 'increasing';
  if (percentChange < -3) return 'decreasing';
  return 'stagnant';
}

/**
 * Validate progressive overload for a specific exercise
 * Checks if volume is increasing over the last 4 weeks
 */
export function validateProgressiveOverload(
  exercise: string,
  last4WeeksSessions: SessionLog[]
): OverloadStatus {
  // Group sessions by week
  const weeklyData = getSessionsInLastNWeeks(last4WeeksSessions, 4);

  // Calculate volume per week for this exercise
  const weeklyVolumes = weeklyData.map(weekSessions => {
    return weekSessions.reduce((sum, session) => {
      if (session.sessionType === 'range' || !session.exercises) return sum; // Type guard
      const exerciseLogs = session.exercises.filter(
        ex => ex.exerciseName.toLowerCase() === exercise.toLowerCase()
      );
      return sum + exerciseLogs.reduce((exSum, ex) => exSum + calculateExerciseVolume(ex), 0);
    }, 0);
  }).filter(v => v > 0); // Only count weeks where exercise was performed

  if (weeklyVolumes.length < 2) {
    return {
      isOnTrack: true, // Not enough data
      weeksStagnant: 0,
      recommendation: 'Continue training to build history',
    };
  }

  // Check if volume is increasing
  const recentVolume = weeklyVolumes[0];
  const olderVolume = weeklyVolumes[weeklyVolumes.length - 1];
  const percentChange = ((recentVolume - olderVolume) / olderVolume) * 100;

  // Count stagnant weeks (< 3% change)
  let weeksStagnant = 0;
  for (let i = 0; i < weeklyVolumes.length - 1; i++) {
    const change = Math.abs(((weeklyVolumes[i] - weeklyVolumes[i + 1]) / weeklyVolumes[i + 1]) * 100);
    if (change < 3) weeksStagnant++;
  }

  const isOnTrack = percentChange >= -5; // Allow up to 5% decrease

  let recommendation = '';
  if (weeksStagnant >= 3) {
    recommendation = 'Volume stagnant for 3+ weeks. Add sets or weight to drive progressive overload.';
  } else if (percentChange < -5) {
    recommendation = 'Volume decreasing. Check recovery and consider volume increase.';
  } else if (percentChange > 10) {
    recommendation = 'Great progress! Volume increasing steadily.';
  } else {
    recommendation = 'Progressive overload on track. Continue current progression.';
  }

  return {
    isOnTrack,
    weeksStagnant,
    recommendation,
  };
}

/**
 * Get volume status for all exercises in recent sessions
 * Useful for post-workout summaries
 */
export function getExerciseVolumeStatus(
  sessions: SessionLog[],
  exerciseNames?: string[]
): ExerciseVolumeStatus[] {
  const last2Weeks = getSessionsInLastNWeeks(sessions, 2);
  if (last2Weeks.length < 2) return [];

  const thisWeekSessions = last2Weeks[0];
  const lastWeekSessions = last2Weeks[1];

  // Get unique exercise names
  const exercises = exerciseNames || Array.from(new Set(
    thisWeekSessions.flatMap(s => {
      if (s.sessionType === 'range' || !s.exercises) return []; // Type guard
      return s.exercises.map(ex => ex.exerciseName);
    })
  ));

  return exercises.map(exerciseName => {
    // Calculate current week volume
    const currentVolume = thisWeekSessions.reduce((sum, session) => {
      if (session.sessionType === 'range' || !session.exercises) return sum; // Type guard
      const exerciseLogs = session.exercises.filter(
        ex => ex.exerciseName.toLowerCase() === exerciseName.toLowerCase()
      );
      return sum + exerciseLogs.reduce((exSum, ex) => exSum + calculateExerciseVolume(ex), 0);
    }, 0);

    // Calculate last week volume
    const lastWeekVolume = lastWeekSessions.reduce((sum, session) => {
      if (session.sessionType === 'range' || !session.exercises) return sum; // Type guard
      const exerciseLogs = session.exercises.filter(
        ex => ex.exerciseName.toLowerCase() === exerciseName.toLowerCase()
      );
      return sum + exerciseLogs.reduce((exSum, ex) => exSum + calculateExerciseVolume(ex), 0);
    }, 0);

    const percentChange = lastWeekVolume > 0
      ? ((currentVolume - lastWeekVolume) / lastWeekVolume) * 100
      : 0;

    let status: 'increasing' | 'stagnant' | 'decreasing' = 'stagnant';
    if (percentChange > 3) status = 'increasing';
    else if (percentChange < -3) status = 'decreasing';

    // Count weeks stagnant
    const overloadStatus = validateProgressiveOverload(exerciseName, sessions);

    return {
      exerciseName,
      currentVolume,
      lastWeekVolume,
      percentChange,
      status,
      weeksStagnant: overloadStatus.weeksStagnant,
    };
  });
}

/**
 * Calculate total tonnage across multiple sessions
 * This is the SHARED function used by both volume tracking AND ACWR calculations
 * 
 * @param sessions - Array of session logs
 * @returns Total tonnage in lbs
 */
export function calculateTotalSessionsTonnage(sessions: SessionLog[]): number {
  return sessions.reduce((sum, session) => {
    if (session.sessionType === 'range' || !session.exercises) return sum;
    return sum + calculateSessionVolume(session.exercises);
  }, 0);
}

/**
 * STRENGTH TONNAGE IN POUNDS — the single definition for anything a user sees
 * labelled "Volume · lbs".
 *
 * There were three separate implementations of "weekly volume" and all three
 * mixed incommensurable units into one pounds-denominated number:
 *   - `(load lbs × distance metres) / 10` for rucks, carries and loaded cardio,
 *     so a 5-mile ruck at 45 lb added roughly 36,200 to a "lbs" total for a
 *     session with no lifting
 *   - a flat 2,000 for every range session, i.e. for turning up
 * They also disagreed with each other on which session types to include and on
 * whether legacy pre-`sets` records counted, so the Training tab could show two
 * different numbers for the same week about 200px apart.
 *
 * This counts exactly one thing: weight × reps (with per-hand, unilateral,
 * drop-segment and superset handling, via calculateExerciseVolume). Distance
 * work is real training but it is not pounds — it is surfaced separately as
 * ruck load-miles, and session count is surfaced separately again.
 */
export function calculateStrengthTonnageLbs(sessions: SessionLog[]): number {
  return sessions.reduce((sum, session) => {
    if (session.sessionType === 'range') return sum;
    const exercises = (session as any).exercises as ExerciseLog[] | undefined;
    if (!Array.isArray(exercises)) return sum;
    return sum + exercises.reduce((exSum, ex) => {
      // calculateExerciseVolume returns 0 for pure distance/timed work, which
      // is the intent here — no carry-distance fallback.
      const v = calculateExerciseVolume(ex);
      return exSum + (v > 0 ? v : 0);
    }, 0);
  }, 0);
}

/**
 * Format volume for display (e.g., "127,450 lbs")
 */
export function formatVolume(volume: number): string {
  return `${Math.round(volume).toLocaleString()} lbs`;
}

/**
 * Format percent change for display (e.g., "+8.5%" or "-3.2%")
 */
export function formatPercentChange(percentChange: number): string {
  const sign = percentChange >= 0 ? '+' : '';
  return `${sign}${percentChange.toFixed(1)}%`;
}

/**
 * Get emoji for volume trend
 */
export function getVolumeTrendEmoji(trend: 'increasing' | 'stagnant' | 'decreasing'): string {
  switch (trend) {
    case 'increasing': return '↗';
    case 'decreasing': return '↘';
    case 'stagnant': return '';
  }
}

/**
 * Get emoji for progressive overload status
 */
export function getOverloadStatusEmoji(isOnTrack: boolean): string {
  return isOnTrack ? '' : '';
}

